import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FileText,
  Shield,
  CreditCard,
  Smartphone,
  Database,
  UserCheck,
  CheckCircle,
  Mail,
  Phone,
  ArrowLeft,
  Clock
} from 'lucide-react';

const Terms = () => {
  const sections = [
    {
      title: "Acceptance of Terms",
      icon: <FileText size={24} />,
      content: [
        "By downloading, installing, or using the Wellness Pro mobile application, you agree to be bound by these Terms of Service",
        "If you do not agree to these terms, you may not use our services",
        "We reserve the right to modify these terms at any time with notice",
        "Continued use of the app after changes constitutes acceptance of new terms",
        "These terms apply to all users including spa owners, managers, and staff"
      ]
    },
    {
      title: "Service Description",
      icon: <Smartphone size={24} />,
      content: [
        "Wellness Pro is a comprehensive spa management software solution",
        "Services include appointment scheduling, customer management, and financial tracking",
        "The app provides real-time synchronization across multiple devices",
        "We offer both online and offline functionality for uninterrupted service",
        "Our services are designed specifically for spa and wellness businesses"
      ]
    },
    {
      title: "Trial Period and Data Access",
      icon: <Clock size={24} />,
      content: [
        "New users receive a 60-day free trial with full access to all features",
        "During the trial period, you can use all premium features without charge",
        "If you choose not to continue with a paid subscription after the trial period, your account will be suspended",
        "To access your data after trial expiration without subscribing, you must pay for the trial period usage at our standard monthly rate",
        "Data export is available for a fee equivalent to one month of service",
        "We will retain your data for 30 days after trial expiration before permanent deletion"
      ]
    },
    {
      title: "Subscription and Payment",
      icon: <CreditCard size={24} />,
      content: [
        "Subscription plans are available on monthly, semi-annual, and annual billing cycles",
        "All payments are processed securely through our payment partners",
        "Prices are subject to change with 30 days notice to existing subscribers",
        "Failed payments may result in service suspension after a grace period",
        "Refunds are available within 7 days of initial subscription",
        "Cancellation can be done through your account settings or by contacting support"
      ]
    },
    {
      title: "User Responsibilities",
      icon: <UserCheck size={24} />,
      content: [
        "You are responsible for maintaining the confidentiality of your account credentials",
        "You must provide accurate and complete information during registration",
        "You agree to use the service only for lawful business purposes",
        "You are responsible for all activities that occur under your account",
        "You must comply with all applicable local, state, and federal laws",
        "You agree not to share your account with unauthorized users"
      ]
    },
    {
      title: "Data and Privacy",
      icon: <Database size={24} />,
      content: [
        "We collect and process data as described in our Privacy Policy",
        "You retain ownership of your business data stored in our system",
        "We implement industry-standard security measures to protect your data",
        "You are responsible for backing up your critical business data",
        "We may use anonymized data to improve our services",
        "Data export is available in standard formats upon request"
      ]
    },
    {
      title: "Prohibited Uses",
      icon: <Shield size={24} />,
      content: [
        "You may not use the service for any illegal or unauthorized purpose",
        "You may not attempt to gain unauthorized access to our systems",
        "You may not use the service to transmit harmful or malicious code",
        "You may not reverse engineer, decompile, or disassemble the software",
        "You may not use the service to compete with Wellness Pro",
        "You may not share your account credentials with competitors"
      ]
    },
    {
      title: "Intellectual Property",
      icon: <Shield size={24} />,
      content: [
        "Wellness Pro and all related trademarks are owned by us",
        "The software and all content are protected by copyright laws",
        "You may not copy, modify, or distribute our software without permission",
        "You grant us a license to use your business data to provide services",
        "Any feedback or suggestions you provide may be used by us without compensation",
        "Third-party integrations are subject to their respective terms"
      ]
    }
  ];

  const additionalTerms = [
    {
      title: "Service Availability",
      content: "We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. Scheduled maintenance will be announced in advance."
    },
    {
      title: "Limitation of Liability",
      content: "Our liability is limited to the amount you paid for the service in the 12 months preceding the claim. We are not liable for indirect or consequential damages."
    },
    {
      title: "Termination",
      content: "Either party may terminate this agreement at any time. Upon termination, your access to the service will cease, and data retention policies will apply."
    },
    {
      title: "Governing Law",
      content: "These terms are governed by the laws of India. Any disputes will be resolved through binding arbitration in accordance with Indian arbitration laws."
    }
  ];

  const contactInfo = [
    {
      icon: <Mail size={20} />,
      title: "Email Us",
      details: ["support@wellnessproapp.com"]
    },
    {
      icon: <Phone size={20} />,
      title: "Call Us",
      details: ["+91 9311252321", "Mon-Fri: 9 AM - 6 PM IST"]
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-200">
        <div className="container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                <FileText className="w-8 h-8" />
              </div>
            </div>
            <h1 className="text-large text-gradient mb-4">
              Terms of Service
            </h1>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              These terms govern your use of the Wellness Pro mobile application and services. Please read them carefully before using our platform.
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-sm text-gray-500">
              <span>Last updated:</span>
              <span className="font-semibold">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Main Terms Sections */}
      <section className="section-padding bg-white">
        <div className="container max-w-4xl">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-card border border-gray-200 hover:shadow-card-hover transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white">
                    {section.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                </div>
                
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Additional Terms */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="container max-w-4xl">
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Additional Terms</h2>
            
            {additionalTerms.map((term, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{term.title}</h3>
                <p className="text-gray-700 leading-relaxed">{term.content}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container max-w-4xl">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-4">
              Questions About Terms?
            </h2>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              If you have any questions about these Terms of Service or need clarification on any points, please contact our legal team.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-6">
                  <div className="w-8 h-8 text-white">
                    {info.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-gray-600 leading-relaxed">{detail}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Link 
              to="/contact" 
              className="btn btn-primary btn-large inline-flex items-center gap-2"
            >
              Contact Legal Team
              <ArrowLeft className="rotate-180" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
